import React from 'react';

import i18n from '@client/utils/i18n';
import { ProjectStatus } from '@ledium/types';
import ArticleIcon from '@mui/icons-material/Article';
import { CircularProgress, IconButton, Paper, Popper, Table, TableBody, TableCell, TableRow } from '@mui/material';

export interface SectionDliInfo {
    id: string;
    name: string;
    repeatNum: number;
    dliPerDay: number;
}

interface DliInfoPopperProps {
    currentDli: number;
    projectStatus: Partial<ProjectStatus>;
    sectionDlis: SectionDliInfo[] | null;
    sectionDlisLoading: boolean;
    onOpen: () => void;
}

const DliInfoPopper = ({ currentDli, projectStatus, sectionDlis, sectionDlisLoading, onOpen }: DliInfoPopperProps) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (anchorEl) {
            setAnchorEl(null);
        } else {
            onOpen();
            setAnchorEl(event.currentTarget);
        }
    };

    const totalDli = sectionDlis ? Math.round(sectionDlis.reduce((a, s) => a + s.dliPerDay * s.repeatNum, 0) * 100) / 100 : null;

    const cellStyle = { padding: '3px 10px' };

    return (
        <>
            <b>{currentDli}</b> {i18n('dli_unit')}
            <IconButton size="small" color="primary" onClick={onClick}>
                <ArticleIcon fontSize="inherit" />
            </IconButton>
            <Popper open={!!anchorEl} anchorEl={anchorEl} placement="bottom-start" style={{ zIndex: 1000 }}>
                <Paper sx={{ border: 1, py: 1 }} onClick={onClick as unknown as React.MouseEventHandler<HTMLDivElement>} style={{ cursor: 'pointer' }}>
                    {projectStatus.section && (
                        <div style={{ padding: '4px 10px', fontSize: '13px' }}>
                            <b>{i18n('dli_current_section_day')}</b>
                            {': '}
                            {projectStatus.section.name}
                            {', '}
                            {projectStatus.section.currentRepeatNum}
                            {' / '}
                            {projectStatus.section.repeatNum}
                        </div>
                    )}
                    {sectionDlisLoading && <CircularProgress size={20} sx={{ m: 1 }} />}
                    {sectionDlis && (
                        <Table size="small">
                            <TableBody>
                                {sectionDlis.map((section) => (
                                    <React.Fragment key={section.id}>
                                        <TableRow>
                                            <TableCell style={cellStyle}>{section.name}</TableCell>
                                            <TableCell style={cellStyle} align="right">
                                                <b>{section.dliPerDay}</b>
                                            </TableCell>
                                            <TableCell style={cellStyle}>{i18n('dli_unit')}</TableCell>
                                        </TableRow>
                                        {section.repeatNum > 1 && (
                                            <TableRow>
                                                <TableCell style={cellStyle}>
                                                    {section.name}
                                                    {' \u00d7 '}
                                                    {section.repeatNum}
                                                </TableCell>
                                                <TableCell style={cellStyle} align="right">
                                                    <b>{Math.round(section.dliPerDay * section.repeatNum * 100) / 100}</b>
                                                </TableCell>
                                                <TableCell style={cellStyle}>{i18n('dli_total_unit')}</TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                                {totalDli !== null && (
                                    <TableRow>
                                        <TableCell style={cellStyle}>
                                            <b>{i18n('dli_sum_all_sections')}</b>
                                        </TableCell>
                                        <TableCell style={cellStyle} align="right">
                                            <b>{totalDli}</b>
                                        </TableCell>
                                        <TableCell style={cellStyle}>{i18n('dli_total_unit')}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </Paper>
            </Popper>
        </>
    );
};

export default DliInfoPopper;
